/**
 * Building blocks that only the Domains screens use (Figma "Cortox Grip Customer Portal" → Domains /
 * Domain Control Centre): headings with a tile, the progress steps, notices and the connect rows. The
 * general pieces (buttons, pills, tabs, forms, modals) are the shared kit in `components/ui`.
 */
import type { ReactNode } from "react";
import { Link } from "react-router";

import { Badge, Section, cx } from "@/components/ui";

/** A Figma icon from public/icons/domains, at the size it was drawn. */
export function DomainIcon({ name, size, className }: { name: string; size: number; className?: string }) {
  return (
    <img
      src={`/icons/domains/${name}.svg`}
      alt=""
      width={size}
      height={size}
      className={cx("shrink-0", className)}
    />
  );
}

export function StepPill({
  state,
}: {
  state: "completed" | "in_progress" | "not_yet" | "pending" | "failed";
}) {
  const map = {
    completed: ["Completed", "success"],
    in_progress: ["In progress", "warning"],
    not_yet: ["Not yet", "neutral"],
    pending: ["Pending", "neutral"],
    failed: ["Failed", "danger"],
  } as const;
  const [label, tone] = map[state];
  return (
    <Badge tone={tone} size="md">
      {label}
    </Badge>
  );
}

type TileTone = "brand" | "pending" | "success" | "danger" | "neutral";
const TILE: Record<TileTone, string> = {
  brand: "bg-[#fef8f6] text-[#2e0d05]",
  pending: "bg-[#fef4e5] text-[#2e0d05]",
  success: "bg-[#eef8f3] text-[#007955]",
  danger: "bg-[#fff1ee] text-[#d6342f]",
  neutral: "bg-[#faf6ef] text-[#5c5551]",
};

/** The 40px square at the start of a heading: initials, an icon, or the business logo. */
export function HeadTile({ tone = "brand", children }: { tone?: TileTone; children: ReactNode }) {
  return (
    <div
      className={cx(
        "flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-[11.25px] text-lg font-medium",
        TILE[tone],
      )}
    >
      {children}
    </div>
  );
}

export function Heading({
  tile,
  title,
  status,
  subtitle,
  actions,
  size = 28,
}: {
  tile: ReactNode;
  title: ReactNode;
  status?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  size?: 28 | 24;
}) {
  return (
    <div className="flex flex-wrap items-start gap-3.5">
      {tile}
      <div className="flex min-w-0 flex-1 flex-col gap-[5px]">
        <div className="flex flex-wrap items-center gap-2.5">
          <h1
            className={cx(
              // A long domain name still breaks to fit; words in a sentence ("Order … awaiting payment") wrap whole.
              "min-w-0 font-semibold wrap-anywhere text-fg",
              size === 28 ? "text-[28px] leading-[34px] tracking-[-0.3px]" : "text-2xl leading-[30px]",
            )}
          >
            {title}
          </h1>
          {status}
        </div>
        {subtitle && <p className="text-sm text-fg-subtle">{subtitle}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2.5">{actions}</div>}
    </div>
  );
}

/** The page column: breadcrumb, heading and sections 24px apart (Figma "Content"). */
export function DomainPage({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-6">{children}</div>;
}

/** Main column + 344px side column, as in the Domain Control Centre frames. */
export function TwoColumns({ main, side }: { main: ReactNode; side?: ReactNode }) {
  return (
    <div className="flex flex-col gap-4 xl:flex-row xl:items-start">
      <div className="flex min-w-0 flex-1 flex-col gap-6">{main}</div>
      {side && <div className="flex w-full shrink-0 flex-col gap-5 xl:w-[344px]">{side}</div>}
    </div>
  );
}

/** A row in a divided list (progress steps, receipts): the first has no rule above it. */
export function RuleRow({
  children,
  first,
  className,
}: {
  children: ReactNode;
  first?: boolean;
  className?: string;
}) {
  return (
    <div className={cx("flex items-center gap-3.5", !first && "border-t border-border pt-3.5", className)}>
      {children}
    </div>
  );
}

export interface Step {
  label: ReactNode;
  state: "completed" | "in_progress" | "not_yet" | "pending" | "failed";
}

/** Figma "Registration progress" / "Connection progress" / "Setup progress". */
export function ProgressSection({
  title,
  steps,
  footnote,
  children,
}: {
  title: string;
  steps: Step[];
  footnote?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <Section title={title}>
      {steps.map((s, i) => (
        <RuleRow key={i} first={i === 0}>
          <p className="min-w-0 flex-1 text-sm font-medium text-[#5c5551]">{s.label}</p>
          <StepPill state={s.state} />
        </RuleRow>
      ))}
      {footnote && <p className="text-xs text-fg-subtle">{footnote}</p>}
      {children}
    </Section>
  );
}

/** Coloured message boxes. `processing` has the spinning gold circle of the "…ing" screens. */
export function Notice({
  tone,
  title,
  children,
  actions,
}: {
  tone: "danger" | "warning" | "success" | "processing" | "cream";
  title: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
}) {
  if (tone === "processing") {
    return (
      <div className="flex flex-col gap-4 rounded-[18px] border border-[#f4b53f] bg-[#fef4e5] p-[22px]">
        <div className="flex items-center gap-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-[#f4b53f]">
            <DomainIcon name="loading" size={20} className="animate-[spin_2.4s_linear_infinite]" />
          </span>
          <p className="text-lg font-semibold text-fg">{title}</p>
        </div>
        {children && <div className="text-sm text-[#5c5551]">{children}</div>}
        {actions && <div className="flex flex-wrap gap-2.5">{actions}</div>}
      </div>
    );
  }
  const box = {
    danger: "border border-[#ffc4bb] bg-[#fff1ee]",
    warning: "border border-[#f4b53f] bg-[#fef4e5]",
    success: "bg-[#eef8f3]",
    cream: "bg-[#faf6ef]",
  }[tone];
  return (
    <div className={cx("flex flex-col gap-3 rounded-[14px] p-4", box)}>
      <p
        className={cx(
          "font-semibold text-fg",
          tone === "success" || tone === "cream" ? "text-[15px]" : "text-base",
        )}
      >
        {title}
      </p>
      {children && <div className="max-w-[640px] text-sm text-[#766e6a]">{children}</div>}
      {actions && <div className="flex flex-wrap gap-2.5">{actions}</div>}
    </div>
  );
}

/** Icon tile + title/subtitle + status (+ action): "What's connected", "Connection". */
export function ConnectRow({
  icon,
  title,
  detail,
  status,
  action,
  first,
  tile = "neutral",
}: {
  icon: ReactNode;
  title: ReactNode;
  detail?: ReactNode;
  status?: ReactNode;
  action?: ReactNode;
  first?: boolean;
  tile?: "neutral" | "success" | "warning" | "danger";
}) {
  const tiles = {
    neutral: "bg-[#faf6ef]",
    success: "bg-[#eef8f3]",
    warning: "bg-[#fef4e5]",
    danger: "bg-[#fff1ee]",
  };
  return (
    <div
      className={cx(
        "flex flex-wrap items-center gap-3.5 sm:flex-nowrap",
        !first && "border-t border-border pt-4",
      )}
    >
      <span className={cx("flex size-10 shrink-0 items-center justify-center rounded-[11px]", tiles[tile])}>
        {icon}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="text-base font-semibold text-fg">{title}</p>
        {detail && <p className="text-[13px] text-fg-subtle">{detail}</p>}
      </div>
      {status}
      {action}
    </div>
  );
}

export function QuickTile({ title, detail, to }: { title: string; detail: ReactNode; to: string }) {
  return (
    <Link
      to={to}
      className="flex min-w-0 flex-1 flex-col gap-1.5 rounded-xl bg-[#faf6ef] p-3.5 transition-colors hover:bg-[#f3ebe7]"
    >
      <span className="text-sm font-semibold text-fg">{title}</span>
      <span className="truncate text-xs text-fg-subtle">{detail}</span>
    </Link>
  );
}

/** Small boxes inside modals: green hint, gold caution, red conflict. */
export function Hint({
  tone,
  title,
  children,
}: {
  tone: "success" | "warning" | "danger";
  title: ReactNode;
  children?: ReactNode;
}) {
  const box = {
    success: "bg-[#eef8f3] [&_.t]:text-[#007955]",
    warning: "bg-[#fef4e5] [&_.t]:text-[#995c05]",
    danger: "border border-[#ffc4bb] bg-[#fff1ee] [&_.t]:text-fg",
  }[tone];
  return (
    <div className={cx("flex flex-col gap-2 rounded-xl p-3.5 text-xs", box)}>
      <p className="t font-semibold">{title}</p>
      {children && <div className="text-[#5c5551]">{children}</div>}
    </div>
  );
}

/** "A · @ · 76.223.100.4" in the beige record chips of the connect screens. */
export function RecordChip({
  type,
  host,
  value,
  strike,
}: {
  type: string;
  host: string;
  value: string;
  strike?: boolean;
}) {
  return (
    <div className="flex max-w-full items-center gap-4 self-start rounded-lg bg-[#f3ebe7] px-2.5 py-2 text-xs text-[#5c5551]">
      <span className="w-12 font-semibold text-fg">{type}</span>
      <span className="w-20 truncate">{host}</span>
      <span className={cx("min-w-0 truncate font-mono", strike && "line-through")}>{value}</span>
    </div>
  );
}
